/**
 * Zoomo Rides shared components, built on the tokens (src/theme/tokens.ts) and the UI layer (src/ui.css).
 * Every screen is composed from these, so a change here reaches the whole app.
 */
import { useCallback, useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type PointerEvent as RPointerEvent, type ReactNode } from 'react';
import { Link } from '@tanstack/react-router';
import { Icon, type IconName } from './Icon';
import { initials, plateText } from '../lib/format';
import { useT } from '../lib/i18n';
import type { VehicleType } from '../lib/types';
import { VehicleArt } from '../art/Vehicle';
import { useLayout } from '../lib/layout';
import { setSheetInset } from '../lib/sheetInset';
import { rubber, settle, snapPoints, velocityOf, type SnapName, type SnapPoint } from '../lib/sheetSnap';

const cx = (...c: (string | false | null | undefined)[]) => c.filter(Boolean).join(' ');

// ── buttons ───────────────────────────────────────────────────────────────

type ButtonProps = {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'secondary' | 'tonal' | 'ghost';
  icon?: IconName;
  iconAfter?: IconName;
  disabled?: boolean;
  /** shows a spinner and blocks taps */
  busy?: boolean;
  /** looks enabled but says why it can't go yet (no dimming flash while typing) */
  soft?: boolean;
  type?: 'button' | 'submit';
  style?: CSSProperties;
  className?: string;
  label?: string;
  testId?: string;
};

export function Button({ children, onClick, variant = 'primary', icon, iconAfter, disabled, busy, soft, type = 'button', style, className, label, testId }: ButtonProps) {
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      aria-label={label}
      data-testid={testId}
      className={cx('zr-btn', `zr-btn--${variant}`, soft && 'is-soft', className)}
      style={style}
    >
      {busy ? (
        <Spinner />
      ) : (
        <>
          {icon && <Icon name={icon} size={variant === 'primary' ? 20 : 18} />}
          {children}
          {iconAfter && <Icon name={iconAfter} size={20} />}
        </>
      )}
    </button>
  );
}

/** Round 44 px icon button (flat on plain screens, floating over a map). */
export function RoundButton({ icon, label, onClick, floating, style, size = 22 }: { icon: IconName; label: string; onClick: () => void; floating?: boolean; style?: CSSProperties; size?: number }) {
  return (
    <button type="button" aria-label={label} onClick={onClick} className={cx('zr-round', floating && 'zr-round--float')} style={style}>
      <Icon name={icon} size={size} />
    </button>
  );
}

export function BackButton({ onClick, floating, style }: { onClick: () => void; floating?: boolean; style?: CSSProperties }) {
  const t = useT();
  return <RoundButton icon="back" label={t('common.back')} onClick={onClick} floating={floating} style={style} />;
}

/** The 44 px bar under the status bar: back on the left, a title in the middle or a note on the right. */
export function TopBar({ onBack, title, right, floating, style }: { onBack?: () => void; title?: ReactNode; right?: ReactNode; floating?: boolean; style?: CSSProperties }) {
  return (
    <header className="zr-gutter" style={{ position: 'relative', flex: 'none', height: 44, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, ...style }}>
      {onBack ? <BackButton onClick={onBack} floating={floating} /> : <span />}
      {title != null && (
        <h1 style={{ position: 'absolute', left: 72, right: 72, margin: 0, textAlign: 'center', fontSize: 17, lineHeight: '22px', fontWeight: 700, letterSpacing: '-0.01em' }} className="zr-ellipsis">
          {title}
        </h1>
      )}
      {right ?? <span />}
    </header>
  );
}

export function Spinner({ size = 22 }: { size?: number }) {
  const t = useT();
  return <span aria-label={t('common.loading')} role="status" style={{ width: size, height: size, borderRadius: '50%', border: '2.5px solid currentColor', borderRightColor: 'transparent', animation: 'zr-spin .7s linear infinite', display: 'inline-block' }} />;
}

// ── small parts ─────────────────────────────────────────────────────────

/** Indian number plate as in the mockup: blue IND strip, black-edged white plate. */
export function Plate({ plate }: { plate: string }) {
  const text = plateText(plate);
  return (
    <span role="img" aria-label={`Number plate ${text}`} style={{ flex: 'none', display: 'inline-flex', alignItems: 'stretch', height: 28, borderRadius: 5, background: '#FFFFFF', boxShadow: 'inset 0 0 0 1.5px #0D0D0D, 0 1px 2px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
      <span aria-hidden="true" style={{ width: 15, background: '#1F4FB5', color: '#FFFFFF', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', gap: 3, paddingBottom: 4, boxSizing: 'border-box', borderRadius: '5px 0 0 5px' }}>
        <span style={{ width: 6, height: 6, borderRadius: 3, boxSizing: 'border-box', border: '1px solid rgba(255,255,255,0.85)' }} />
        <span style={{ fontSize: 5.5, lineHeight: '6px', fontWeight: 700, letterSpacing: '0.02em' }}>IND</span>
      </span>
      <span aria-hidden="true" style={{ padding: '0 7px', display: 'flex', alignItems: 'center', fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', color: '#0D0D0D', whiteSpace: 'nowrap' }}>{text}</span>
    </span>
  );
}

/** Driver/rider picture. The API has no photos yet, so this is the initials disc. */
export function Avatar({ name, size = 48, tone = 'soft' }: { name: string | null | undefined; size?: number; tone?: 'soft' | 'solid' }) {
  return (
    <span
      aria-hidden="true"
      style={{
        flex: 'none', width: size, height: size, borderRadius: size / 2,
        background: tone === 'solid' ? 'var(--pri)' : 'var(--acSoft)', color: tone === 'solid' ? 'var(--priInk)' : 'var(--acText)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: Math.round(size * (tone === 'solid' ? 0.34 : 0.36)), fontWeight: 900, letterSpacing: '-0.02em',
      }}
    >
      {initials(name) || '·'}
    </span>
  );
}

/** iOS-style switch from the mockup (52×32 track inside a 44 px target). */
export function Toggle({ on, onChange, label, disabled }: { on: boolean; onChange: (v: boolean) => void; label: string; disabled?: boolean }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      aria-disabled={disabled || undefined}
      onClick={() => !disabled && onChange(!on)}
      style={{ flex: 'none', width: 60, height: 44, marginRight: -4, border: 0, padding: 0, background: 'transparent', position: 'relative', cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.5 : 1 }}
    >
      <span aria-hidden="true" style={{ position: 'absolute', left: 4, top: 6, width: 52, height: 32, borderRadius: 16, background: on ? 'var(--on)' : 'var(--s3)', transition: 'background-color .2s var(--ease)' }} />
      <span aria-hidden="true" style={{ position: 'absolute', top: 9, left: 7, width: 26, height: 26, borderRadius: 13, background: '#FFFFFF', boxShadow: '0 1px 3px rgba(0,0,0,0.25)', transform: on ? 'translateX(20px)' : 'none', transition: 'transform .22s var(--ease-out)' }} />
    </button>
  );
}

export const Handle = () => <div aria-hidden="true" className="zr-handle" />;

/** Small aqua tag ("Looking", "soon"). */
export const Tag = ({ children, style }: { children: ReactNode; style?: CSSProperties }) => (
  <span className="zr-tag" style={style}>
    {children}
  </span>
);

/** Rounded chip button ("Set on map", "Ghar"). */
export function Chip({ icon, children, onClick, label, style }: { icon?: IconName; children: ReactNode; onClick?: () => void; label?: string; style?: CSSProperties }) {
  return (
    <button type="button" className="zr-chip" onClick={onClick} aria-label={label} style={style}>
      {icon && <Icon name={icon} size={18} />}
      {children}
    </button>
  );
}

/** Pill floating over a map. */
export function FloatPill({ children, style, live }: { children: ReactNode; style?: CSSProperties; live?: boolean }) {
  return (
    <div className="zr-pill" style={style} aria-live={live ? 'polite' : undefined}>
      {children}
    </div>
  );
}

// ── inline notes (no toasts, no "coming soon" popups) ─────────────────────

export type Note = { text: string; tone?: 'info' | 'error' | 'good'; id: number } | null;

/** A line of text right where the rider acted. `say` shows it (and clears it after a while unless it's an error). */
export function useNote(ms = 5000): [Note, (text: string | null, tone?: 'info' | 'error' | 'good') => void] {
  const [note, setNote] = useState<Note>(null);
  const timer = useRef<number | undefined>(undefined);
  const say = useCallback(
    (text: string | null, tone: 'info' | 'error' | 'good' = 'info') => {
      window.clearTimeout(timer.current);
      setNote(text ? { text, tone, id: Date.now() } : null);
      if (text && tone !== 'error') timer.current = window.setTimeout(() => setNote(null), ms);
    },
    [ms],
  );
  useEffect(() => () => window.clearTimeout(timer.current), []);
  return [note, say];
}

export function InlineNote({ note, style, icon }: { note: Note; style?: CSSProperties; icon?: IconName }) {
  if (!note) return null;
  const tone = note.tone ?? 'info';
  return (
    <p key={note.id} role={tone === 'error' ? 'alert' : 'status'} className={cx('zr-note', tone === 'error' && 'zr-note--error', tone === 'good' && 'zr-note--good')} style={style}>
      <Icon name={icon ?? (tone === 'error' ? 'alert' : tone === 'good' ? 'check' : 'info')} size={16} sw={2.2} />
      <span>{note.text}</span>
    </p>
  );
}

// ── sheets ────────────────────────────────────────────────────────────────

/** Modal bottom sheet (forms and choices: name, language, payment, safety). Rises in, slides out. */
export function Sheet({ open, onClose, title, children }: { open: boolean; onClose: () => void; title: string; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [shown, setShown] = useState(open);
  const [closing, setClosing] = useState(false);
  useEffect(() => {
    if (open) {
      setShown(true);
      setClosing(false);
    } else if (shown) {
      setClosing(true);
      const reduce = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
      const tm = window.setTimeout(() => (setShown(false), setClosing(false)), reduce ? 0 : 180);
      return () => window.clearTimeout(tm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const prev = document.activeElement as HTMLElement | null;
    ref.current?.focus();
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      prev?.focus?.();
    };
  }, [open, onClose]);
  if (!shown) return null;
  return (
    <div style={{ position: 'absolute', inset: 0, zIndex: 50 }}>
      <div className={cx('zr-scrim', closing && 'is-closing')} onClick={onClose} />
      <div ref={ref} role="dialog" aria-modal="true" aria-label={title} tabIndex={-1} className={cx('zr-modal', closing && 'is-closing')}>
        <Handle />
        <h2 style={{ margin: '20px 0 0', fontSize: 22, lineHeight: '28px', fontWeight: 700, letterSpacing: '-0.015em' }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}

export const sheetText: CSSProperties = { margin: '8px 0 0', fontSize: 15, lineHeight: '22px', fontWeight: 500, color: 'var(--ink2)' };

/**
 * The sheet that sits over the shared map. It takes its content's natural height (so it fits any phone) and tells
 * the map how much it covers, so routes are framed above it. `art` is the strip of hills drawn on its top edge.
 *
 * Phones: it can be dragged (handle or any non-control part) between open, half (tall sheets) and peek, with a
 * flick going on to the next stop; the main button (`SheetFoot`) stays pinned at the bottom while it peeks. The
 * drag moves it with a transform only, and the map is told the new size once it settles. Wide screens: it is the
 * side panel and doesn't drag.
 */
export function MapSheet({ children, label, onHeight, art, style, maxHeight = 'calc(100% - var(--sat) - 64px)', testId, peek = 72 }: { children: ReactNode; label: string; onHeight?: (h: number) => void; art?: ReactNode; style?: CSSProperties; maxHeight?: string; testId?: string; peek?: number }) {
  const t = useT();
  const ref = useRef<HTMLElement>(null);
  const artRef = useRef<HTMLDivElement>(null);
  const L = useLayout();
  const draggable = !L.wide;
  const [h, setH] = useState(0);
  const [snap, setSnap] = useState<SnapName>('open');
  const [stops, setStops] = useState(1);
  const off = useRef(0);
  const snapRef = useRef<SnapName>('open');
  const cb = useRef(onHeight);
  cb.current = onHeight;

  const points = useCallback((): SnapPoint[] => {
    const el = ref.current;
    if (!el || !draggable) return [{ name: 'open', offset: 0 }];
    const foot = el.querySelector<HTMLElement>('.zr-sheet-foot');
    const sab = parseFloat(getComputedStyle(el).paddingBottom) || 0;
    const vh = el.parentElement?.clientHeight || window.innerHeight;
    return snapPoints(el.offsetHeight, vh, peek + (foot?.offsetHeight ?? 0) + sab);
  }, [draggable, peek]);

  /** Put the sheet (and its hill strip) at `y`; the pinned footer moves the other way by the same amount. */
  const place = useCallback((y: number, ms: number) => {
    off.current = y;
    const tr = ms ? `transform ${ms}ms var(--ease-out)` : 'none';
    for (const n of [ref.current, artRef.current]) {
      if (!n) continue;
      n.style.transition = tr;
      n.style.transform = y ? `translate3d(0, ${y}px, 0)` : '';
    }
    ref.current?.style.setProperty('--sheet-off', `${Math.max(0, y)}px`);
  }, []);

  /** Rest at a stop and tell the map how much of it is now covered. */
  const rest = useCallback(
    (p: SnapPoint, ms = 340) => {
      place(p.offset, ms);
      snapRef.current = p.name;
      setSnap(p.name);
      const el = ref.current;
      if (el && !L.wide) setSheetInset(el.offsetHeight - p.offset);
    },
    [place, L.wide],
  );

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const report = () => {
      const v = Math.round(el.getBoundingClientRect().height);
      setH(v);
      cb.current?.(v);
      // the content changed size: stay at the same named stop (or open if it no longer exists)
      const p = points();
      setStops(p.length);
      rest(p.find((x) => x.name === snapRef.current) ?? p[0], 0);
    };
    report();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(report);
    ro.observe(el);
    return () => ro.disconnect();
  }, [points, rest]);
  // the next screen reports its own sheet; until then the map has all the room
  useEffect(() => () => setSheetInset(0), []);
  useEffect(() => {
    if (!draggable) place(0, 0);
  }, [draggable, place]);

  const drag = useRef<{ id: number; x: number; y: number; from: number; on: boolean; samples: { y: number; t: number }[]; body: HTMLElement | null } | null>(null);
  const onPointerDown = (e: RPointerEvent<HTMLElement>) => {
    if (!draggable || (e.pointerType === 'mouse' && e.button !== 0)) return;
    const target = e.target as HTMLElement;
    if (target.closest('input, textarea, select, [contenteditable]')) return;
    const body = target.closest<HTMLElement>('.zr-sheet-body');
    drag.current = { id: e.pointerId, x: e.clientX, y: e.clientY, from: off.current, on: false, samples: [{ y: e.clientY, t: e.timeStamp }], body };
  };
  const onPointerMove = (e: RPointerEvent<HTMLElement>) => {
    const d = drag.current;
    if (!d || d.id !== e.pointerId) return;
    const dy = e.clientY - d.y;
    if (!d.on) {
      if (Math.abs(dy) < 8) return;
      if (Math.abs(e.clientX - d.x) > Math.abs(dy)) return void (drag.current = null);
      // a list that can still scroll that way scrolls instead of moving the sheet
      if (d.body && ((dy > 0 && d.body.scrollTop > 0) || (dy < 0 && d.from === 0 && d.body.scrollHeight > d.body.clientHeight + 1))) return void (drag.current = null);
      d.on = true;
      ref.current?.setPointerCapture?.(e.pointerId);
    }
    const p = points();
    place(rubber(d.from + dy, 0, p[p.length - 1].offset), 0);
    d.samples.push({ y: e.clientY, t: e.timeStamp });
    if (d.samples.length > 8) d.samples.shift();
  };
  const onPointerUp = (e: RPointerEvent<HTMLElement>) => {
    const d = drag.current;
    drag.current = null;
    if (!d || !d.on || d.id !== e.pointerId) return;
    d.samples.push({ y: e.clientY, t: e.timeStamp });
    rest(settle(off.current, velocityOf(d.samples), points()));
    // the drag ended on a button: that is not a tap
    const stop = (ev: Event) => (ev.stopPropagation(), ev.preventDefault());
    window.addEventListener('click', stop, { capture: true, once: true });
    window.setTimeout(() => window.removeEventListener('click', stop, { capture: true }), 60);
  };
  const toggle = () => {
    const p = points();
    rest(snapRef.current === 'open' ? p[p.length - 1] : p[0]);
  };

  return (
    <>
      {art && (
        <div ref={artRef} aria-hidden="true" className="zr-mapsheet-art" style={{ position: 'absolute', left: 0, right: 0, bottom: Math.max(0, h - 28), height: 80, pointerEvents: 'none', zIndex: 9 }}>
          {art}
        </div>
      )}
      <section
        ref={ref}
        aria-label={label}
        data-testid={testId}
        data-snap={snap}
        className="zr-mapsheet"
        style={{ maxHeight, ...style }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={() => (drag.current = null)}
      >
        {draggable && stops > 1 && (
          <button type="button" className="zr-sheet-grab" aria-label={snap === 'open' ? t('sheet.less') : t('sheet.more')} aria-expanded={snap === 'open'} onClick={toggle} />
        )}
        {children}
      </section>
    </>
  );
}

/** The bottom of a map sheet (its main button): stays in view while the sheet is pulled down to peek. */
export const SheetFoot = ({ children, style }: { children: ReactNode; style?: CSSProperties }) => (
  <div className="zr-sheet-foot" style={style}>
    {children}
  </div>
);

// ── rows and cards ──────────────────────────────────────────────────────

/** One row of a grouped list (Account, Safety): icon, title + sub, value, chevron. */
export function ListRow({ icon, title, sub, value, valueTone, chevron = true, onClick, to, right, testId, label, style }: { icon?: IconName; title: ReactNode; sub?: ReactNode; value?: ReactNode; valueTone?: 'ac' | 'ink2'; chevron?: boolean; onClick?: () => void; to?: string; right?: ReactNode; testId?: string; label?: string; style?: CSSProperties }) {
  const body = (
    <>
      {icon && <Icon name={icon} size={20} sw={1.9} style={{ flex: 'none' }} />}
      <span style={{ flex: 1, minWidth: 0 }}>
        <span className="zr-ellipsis" style={{ display: 'block', fontSize: 15, lineHeight: '20px', fontWeight: 700 }}>{title}</span>
        {sub && <span className="zr-ellipsis" style={{ display: 'block', marginTop: 1, fontSize: 13, lineHeight: '18px', fontWeight: 500, color: 'var(--ink2)' }}>{sub}</span>}
      </span>
      {value != null && <span className="zr-ellipsis" style={{ flex: '0 1 auto', minWidth: 0, maxWidth: '52%', textAlign: 'right', fontSize: 14, fontWeight: 500, color: valueTone === 'ac' ? 'var(--acText)' : 'var(--ink2)' }}>{value}</span>}
      {right}
      {chevron && <Icon name="chevronRight" size={18} style={{ flex: 'none', color: 'var(--ink3)' }} />}
    </>
  );
  const s: CSSProperties = { minHeight: sub ? 62 : 58, ...style };
  if (to)
    return (
      <Link to={to} className="zr-row zr-press-soft" data-testid={testId} aria-label={label} style={s}>
        {body}
      </Link>
    );
  if (onClick)
    return (
      <button type="button" className="zr-row zr-press-soft" onClick={onClick} data-testid={testId} aria-label={label} style={s}>
        {body}
      </button>
    );
  return (
    <div className="zr-row" data-testid={testId} style={{ ...s, cursor: 'default' }}>
      {body}
    </div>
  );
}

/** A rounded group of rows. */
export const Group = ({ children, style, label }: { children: ReactNode; style?: CSSProperties; label?: string }) => (
  <div className="zr-group" role={label ? 'group' : undefined} aria-label={label} style={style}>
    {children}
  </div>
);

/** Square aqua icon tile (38 px, radius 13) used at the start of cards. */
export const IconTile = ({ icon, size = 38, style }: { icon: IconName; size?: number; style?: CSSProperties }) => (
  <span aria-hidden="true" className="zr-icontile" style={{ width: size, height: size, borderRadius: Math.round(size * 0.34), ...style }}>
    <Icon name={icon} size={Math.round(size * 0.52)} sw={2} />
  </span>
);

/** Section heading with an optional right-hand note. */
export function SectionHead({ title, right, style }: { title: ReactNode; right?: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 12, ...style }}>
      <h2 style={{ margin: 0, fontSize: 17, lineHeight: '22px', fontWeight: 700, letterSpacing: '-0.01em' }}>{title}</h2>
      {right}
    </div>
  );
}

/** Small uppercase label above a list ("PLACES · FIXED FARE BY AUTO"). */
export const Overline = ({ children, style }: { children: ReactNode; style?: CSSProperties }) => (
  <span style={{ fontSize: 11, lineHeight: '14px', fontWeight: 700, letterSpacing: '0.12em', color: 'var(--ink3)', ...style }}>{children}</span>
);

// ── vehicles ───────────────────────────────────────────────────────────

/** The vehicle's vector drawing (src/art/Vehicle.tsx), fitted into width × height. */
export function VehicleImage({ type, width, height, style }: { type: VehicleType; width: number; height: number; style?: CSSProperties }) {
  return <VehicleArt type={type} width={width} height={height} style={style} />;
}

/** Home grid tile: picture, name, minutes away. */
export function VehicleTile({ type, name, sub, onClick, label }: { type: VehicleType; name: string; sub: string; onClick: () => void; label?: string }) {
  return (
    <button type="button" onClick={onClick} aria-label={label} className="zr-press-soft" style={{ minWidth: 0, border: 0, borderRadius: 20, background: 'var(--s1)', padding: '8px 4px 10px', display: 'flex', flexDirection: 'column', alignItems: 'center', color: 'var(--ink)', cursor: 'pointer', fontFamily: 'inherit' }}>
      <VehicleImage type={type} width={64} height={40} />
      <span style={{ marginTop: 4, fontSize: 14, lineHeight: '18px', fontWeight: 700 }}>{name}</span>
      <span style={{ fontSize: 12, lineHeight: '15px', fontWeight: 500, color: 'var(--ink2)' }}>{sub}</span>
    </button>
  );
}

/** Choose-ride row: the selected one grows into the aqua card with the big fare (animated, transform only). */
export function VehicleRow({ type, name, sub, fare, selected, onSelect, label, style, seats, seatsLabel }: { type: VehicleType; name: string; sub: string; fare: string; selected: boolean; onSelect: () => void; label: string; style?: CSSProperties; seats?: number; seatsLabel?: string }) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      aria-label={label}
      onClick={onSelect}
      className="zr-press-soft zr-vrow"
      style={{
        position: 'relative', flex: 'none', width: '100%', height: selected ? 84 : 58, border: 0, borderRadius: selected ? 20 : 18,
        background: selected ? 'var(--acSoft)' : 'transparent', boxShadow: selected ? 'inset 0 0 0 1.5px var(--sel)' : 'inset 0 0 0 1.5px transparent',
        padding: selected ? '0 18px 0 8px' : '0 18px 0 16px', display: 'flex', alignItems: 'center', gap: selected ? 10 : 14,
        color: 'var(--ink)', textAlign: 'left', cursor: 'pointer', fontFamily: 'inherit',
        transition: 'background-color .22s var(--ease), box-shadow .22s var(--ease), scale .12s var(--ease)',
        ...style,
      }}
    >
      <VehicleImage type={type} width={selected ? 88 : 66} height={selected ? 53 : 40} style={{ transition: 'transform .3s var(--ease-out)' }} />
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: selected ? 18 : 16, lineHeight: selected ? '23px' : '21px', fontWeight: 700 }}>
          {name}
          {seats != null && (
            <span role="img" aria-label={seatsLabel} style={{ display: 'inline-flex', alignItems: 'center', gap: 2, fontSize: 13, fontWeight: 700, color: 'var(--ink2)' }}>
              <Icon name="user" size={13} sw={2.2} />
              {seats}
            </span>
          )}
        </span>
        <span className="zr-ellipsis" style={{ display: 'block', fontSize: 13, lineHeight: '18px', fontWeight: 500, color: 'var(--ink2)' }}>{sub}</span>
      </span>
      <span key={selected ? 'on' : 'off'} className="zr-num zr-tick" style={selected ? { fontSize: 28, lineHeight: '32px', fontWeight: 900, letterSpacing: '-0.03em' } : { fontSize: 17, lineHeight: '21px', fontWeight: 700 }}>
        {fare}
      </span>
    </button>
  );
}

// ── live ride parts ────────────────────────────────────────────────────

/** Driver card: initials, name, rating or vehicle, number plate. */
export function DriverCard({ name, line, plate, size = 48, boxed = true, style }: { name: string; line: ReactNode; plate?: string | null; size?: number; boxed?: boolean; style?: CSSProperties }) {
  return (
    <div className={boxed ? 'zr-card' : undefined} style={{ padding: boxed ? '14px 16px' : 0, display: 'flex', alignItems: 'center', gap: 12, ...style }}>
      <Avatar name={name} size={size} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="zr-ellipsis" style={{ fontSize: size >= 48 ? 17 : 16, lineHeight: size >= 48 ? '22px' : '21px', fontWeight: 700 }}>{name}</div>
        <div className="zr-ellipsis" style={{ marginTop: size >= 48 ? 2 : 0, display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, lineHeight: '18px', fontWeight: 500, color: 'var(--ink2)' }}>{line}</div>
      </div>
      {plate && <Plate plate={plate} />}
    </div>
  );
}

/**
 * The top of a live-ride sheet: a title and line on the left, a big number (minutes) on the right. When the
 * status changes, the words crossfade.
 */
export function StatusHeader({ title, sub, big, unit, bigTone, style }: { title: string; sub: ReactNode; big?: ReactNode; unit?: string; bigTone?: 'ac'; style?: CSSProperties }) {
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, ...style }}>
      <div style={{ minWidth: 0 }} aria-live="polite">
        <h2 key={title} className="zr-tick" style={{ margin: 0, fontSize: 22, lineHeight: '28px', fontWeight: 700, letterSpacing: '-0.015em' }}>{title}</h2>
        <p className="zr-ellipsis" style={{ margin: '4px 0 0', fontSize: 14, lineHeight: '20px', fontWeight: 500, color: 'var(--ink2)' }}>{sub}</p>
      </div>
      {big != null && (
        <div style={{ flex: 'none', display: 'flex', alignItems: 'baseline', gap: 3 }}>
          <span key={String(big)} className="zr-num zr-tick" style={bigTone === 'ac' ? { fontSize: 30, lineHeight: '44px', fontWeight: 900, letterSpacing: '-0.03em', color: 'var(--acText)' } : { fontSize: 44, lineHeight: '44px', fontWeight: 900, letterSpacing: '-0.04em' }}>
            {big}
          </span>
          {unit && <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink2)' }}>{unit}</span>}
        </div>
      )}
    </div>
  );
}

/** Segmented progress ("6 autos asked · 2 looking"): each segment fills with a transform. */
export function Segments({ total, asked, looking, label, testId, data }: { total: number; asked: number; looking: number; label: string; testId?: string; data?: Record<string, string | number> }) {
  const n = Math.max(1, Math.min(total, 10));
  return (
    <div role="img" aria-label={label} data-testid={testId} {...Object.fromEntries(Object.entries(data ?? {}).map(([k, v]) => [`data-${k}`, v]))} style={{ display: 'grid', gridTemplateColumns: `repeat(${n}, minmax(0, 1fr))`, gap: 6 }}>
      {Array.from({ length: n }, (_, i) => {
        const fill = i < looking ? 'var(--ac)' : i < asked ? 'var(--asked)' : 'transparent';
        return (
          <span key={i} className="zr-seg">
            <i style={{ background: fill, transform: i < asked || i < looking ? 'scaleX(1)' : 'scaleX(0)', transitionDelay: `${i * 40}ms` }} />
          </span>
        );
      })}
    </div>
  );
}

// ── tab bar ────────────────────────────────────────────────────────────

/** The floating glass tab bar: Ride · Trips · Account. */
export function BottomNav({ active }: { active: 'ride' | 'trips' | 'account' }) {
  const t = useT();
  const tab = (key: 'ride' | 'trips' | 'account', to: string, label: string, icon: IconName) => {
    const on = active === key;
    return (
      <Link to={to} aria-label={on ? undefined : label} aria-current={on ? 'page' : undefined}>
        <Icon name={icon} size={22} sw={on ? 2.1 : 1.9} />
        {on && <span>{label}</span>}
      </Link>
    );
  };
  return (
    <nav aria-label={t('nav.main')} className="zr-nav">
      {tab('ride', '/home', t('nav.ride'), 'ride')}
      {tab('trips', '/trips', t('nav.trips'), 'history')}
      {tab('account', '/account', t('nav.account'), 'user')}
    </nav>
  );
}
